import React, { useState, useEffect, useRef, useContext, useCallback } from 'react'
import { MAIN_PATHS } from 'src/routes/main'
import { HistoryRouteContext } from 'src/routes'
import { useAuthState } from 'src/providers/AuthProviders'
import { HouseHeader } from 'src/pages/main/house/HouseHeader'
import { HouseGrid3D } from 'src/pages/main/house/HouseGrid3D'
import { HouseRightMenu } from 'src/pages/main/house/HouseRightMenu'
import { HouseItemsPanel } from 'src/pages/main/house/HouseItemsPanel'
import { HouseLayoutControls } from 'src/pages/main/house/HouseLayoutControls'
import { materialCategories } from 'src/pages/main/house/const/houseConstants'
import LayoutImg from 'src/assets/img/house/nav_layout.png'
import GoOutImg from 'src/assets/img/house/nav_odekake.png'
import GachaImg from 'src/assets/img/house/nav_gacha.png'
import HomeImg from 'src/assets/img/house/nav_home.png'
import BtnLayoutFix from 'src/assets/img/house/btn_layout_fix.svg'
import BtnLayoutCancel from 'src/assets/img/house/btn_layout_cancel.svg'
import BtnLayoutReverse from 'src/assets/img/house/btn_layout_reverse.svg'
import BG from 'src/assets/img/house/BG.png'

import ModalUserList from 'src/pages/main/house/ModalUserList'
import ModalUserProfile from 'src/pages/main/house/ModalUserProfile'
import BackroundInProgress from 'src/assets/img/common/bg-house.png'
import ModalRankUp from 'src/pages/main/house/ModalRankUp'
import { useFetchDataHouse } from 'src/pages/main/house/hooks/useFetchDataHouse'
import { useDragAndDrop } from 'src/pages/main/house/hooks/useDragAndDrop'
import { usePinchZoom } from 'src/pages/main/house/hooks/usePinchZoom'
import postSaveMaterialItemPositions from 'src/services/post/postSaveMaterialItemPositions'
import getUserMaterialItemPositions from 'src/services/get/getUserMaterialItemPositions'
import IconCaution from 'src/assets/img/house/icon_caution.svg'
import { useQuery } from 'react-query'
import getCurrentEvents from 'src/services/get/getCurrentEvents'
import { Wrapper } from 'src/components/common/Wrapper'
import { MainLayout } from 'src/components/layouts/MainLayout'
import ModalHelp from 'src/pages/main/house/ModalHelp'
import 'src/pages/main/house/drag.css'

const CONCEPT_SLOTS = ['floor', 'left', 'right']

const findConceptItemById = (items, itemId) => items.find((item) => item.id === itemId) || null

const getConceptSlots = (item) => {
  if (!item) return []

  if (item.individual_space === 'concept') {
    return CONCEPT_SLOTS
  }

  if (item.individual_space === 'wall') {
    if (item.wall_position === 'left') return ['left']
    if (item.wall_position === 'right') return ['right']

    const slots = []
    if (item.left_wall_image_url) slots.push('left')
    if (item.right_wall_image_url) slots.push('right')

    return slots.length > 0 ? slots : ['left']
  }

  return ['floor']
}

const getConceptSlotImage = (item, slot) => {
  if (!item) return null

  if (slot === 'floor') return item.floor_wall_image_url || item.icon_url || null
  if (slot === 'left') return item.left_wall_image_url || item.icon_url || null
  if (slot === 'right') return item.right_wall_image_url || item.icon_url || null

  return null
}

const applyConceptItemToSlots = (currentSlots, item) => {
  const nextSlots = { ...currentSlots }
  const targetSlots = getConceptSlots(item)
  const overlappingItemIds = new Set(targetSlots.map((slot) => nextSlots[slot]).filter(Boolean))

  overlappingItemIds.forEach((itemId) => {
    CONCEPT_SLOTS.forEach((slot) => {
      if (nextSlots[slot] === itemId) {
        nextSlots[slot] = null
      }
    })
  })

  targetSlots.forEach((slot) => {
    nextSlots[slot] = item.id
  })

  return nextSlots
}

const buildConceptPositionsForSave = (slotAssignments, items) => {
  const positions = []
  const processedItemIds = new Set()

  Object.values(slotAssignments).forEach((itemId) => {
    if (!itemId || processedItemIds.has(itemId)) return

    processedItemIds.add(itemId)

    const item = findConceptItemById(items, itemId)
    if (!item) return

    const activeSlots = getConceptSlots(item).filter((slot) => slotAssignments[slot] === itemId)
    if (activeSlots.length === 0) return

    const basePosition = {
      user_character_cloth_id: itemId,
      grid_x: null,
      grid_y: null,
      index: 1,
      reverse_flag: false
    }

    if (
      item.individual_space === 'concept' &&
      activeSlots.includes('floor') &&
      activeSlots.includes('left') &&
      activeSlots.includes('right')
    ) {
      positions.push(basePosition)
      return
    }

    if (item.individual_space === 'wall') {
      const hasLeft = activeSlots.includes('left')
      const hasRight = activeSlots.includes('right')

      if (hasLeft && hasRight) {
        positions.push(basePosition)
      } else if (hasLeft) {
        positions.push({ ...basePosition, wall_position: 'left' })
      } else if (hasRight) {
        positions.push({ ...basePosition, wall_position: 'right' })
      }
      return
    }

    if (activeSlots.includes('floor')) {
      positions.push(basePosition)
    }
  })

  return positions
}

export default function HousePage() {
  const { user } = useAuthState()
  const { redirectTo } = useContext(HistoryRouteContext)
  const pageRootRef = useRef(null)

  const getRoomDimensions = (rank) => {
    switch (rank) {
      case 'bronze':
        return { gridSize: 8, wallRows: 5 }
      case 'silver':
        return { gridSize: 10, wallRows: 5 }
      case 'gold':
        return { gridSize: 12, wallRows: 5 }
      default:
        return { gridSize: 8, wallRows: 5 }
    }
  }

  const { gridSize, wallRows } = getRoomDimensions(user?.ranking)
  const tileSize = 40
  const size = gridSize * tileSize + 10
  const wallHeight = wallRows * tileSize + 10

  const [selectedCategory, setSelectedCategory] = useState('background')
  const [selectedItemId, setSelectedItemId] = useState(null)
  const [isLayoutMode, setIsLayoutMode] = useState(false)
  const [pinnedItemIds, setPinnedItemIds] = useState(new Set())
  const [itemAmounts, setItemAmounts] = useState({})
  const [showInvalidPlacementPopup, setShowInvalidPlacementPopup] = useState(false)
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false)
  const [isUserListModalOpen, setIsUserListModalOpen] = useState(false)
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false)
  const [isRankUpModalOpen, setIsRankUpModalOpen] = useState(false)
  const [isUIOn, setIsUIOn] = useState(true)
  const [pointerDragSession, setPointerDragSession] = useState(null)

  const containerRef = useRef(null)
  const pointerDragSessionRef = useRef(null)
  const hoverPositionRef = useRef(null)
  const ghostItemRef = useRef(null)
  const { isPinching } = usePinchZoom(containerRef, [isLayoutMode])

  const [backgroundImage, setBackgroundImage] = useState('')
  const [savedBackgroundImage, setSavedBackgroundImage] = useState(BackroundInProgress)
  const [floorBackgroundImage, setFloorBackgroundImage] = useState(null)
  const [editingBackgroundItem, setEditingBackgroundItem] = useState(null)
  const [tempBackgroundImage, setTempBackgroundImage] = useState(null)
  const [savedBackgroundItemId, setSavedBackgroundItemId] = useState(null)
  const [savedBackgroundRecordId, setSavedBackgroundRecordId] = useState(null)
  const [leftWallBackgroundImage, setLeftWallBackgroundImage] = useState(null)
  const [rightWallBackgroundImage, setRightWallBackgroundImage] = useState(null)

  const [editingConceptItem, setEditingConceptItem] = useState(null)
  const [tempConceptState, setTempConceptState] = useState(null)

  const [activeConceptItemId, setActiveConceptItemId] = useState(null)
  const [activeLeftWallConceptItemId, setActiveLeftWallConceptItemId] = useState(null)
  const [activeRightWallConceptItemId, setActiveRightWallConceptItemId] = useState(null)

  const [savedConceptItemId, setSavedConceptItemId] = useState(null)
  const [savedConceptRecordId, setSavedConceptRecordId] = useState(null)
  const [savedLeftWallConceptItemId, setSavedLeftWallConceptItemId] = useState(null)
  const [savedLeftWallConceptRecordId, setSavedLeftWallConceptRecordId] = useState(null)
  const [savedRightWallConceptItemId, setSavedRightWallConceptItemId] = useState(null)
  const [savedRightWallConceptRecordId, setSavedRightWallConceptRecordId] = useState(null)

  const [layoutSnapshot, setLayoutSnapshot] = useState(null)

  const { items, rawMaterialItems, refetch } = useFetchDataHouse(user?.id)
  const {
    hoverPosition,
    draggingItem,
    itemsToDestroy,
    itemAttachedAmounts,
    justDroppedKey,
    setItemsToDestroy,
    setItemAttachedAmounts,
    canPlace,
    getItemAtPosition,
    isTileHovered,
    getItemsForPlane,
    setHoverPosition,
    setDraggingItem,
    handleCancel,
    getPositionsForSave,
    loadSavedPositions,
    takeSnapshot,
    ghostItem,
    placeGhostAtCenter,
    convertItemToGhost,
    moveGhost,
    confirmGhost,
    cancelGhost,
    rotateGhost
  } = useDragAndDrop(gridSize, wallRows)

  useEffect(() => {
    pointerDragSessionRef.current = pointerDragSession
  }, [pointerDragSession])

  useEffect(() => {
    hoverPositionRef.current = hoverPosition
  }, [hoverPosition])

  useEffect(() => {
    ghostItemRef.current = ghostItem
  }, [ghostItem])

  const handlePlaceGhost = useCallback(
    (item) => {
      const itemSpace = item.individual_space || 'floor'
      let plane = 'floor'
      if (itemSpace === 'left' || itemSpace === 'right') plane = itemSpace
      else if (itemSpace === 'wall') plane = 'left'
      placeGhostAtCenter(item, plane)
    },
    [placeGhostAtCenter]
  )

  const clearPointerDragSession = useCallback(() => {
    setPointerDragSession(null)
    setDraggingItem(null)
    setHoverPosition(null)
  }, [setDraggingItem, setHoverPosition])

  const getTileFromPoint = useCallback((clientX, clientY) => {
    if (typeof document === 'undefined') return null

    const tileSelector = '[data-house-tile="true"]'
    const tile =
      document
        .elementsFromPoint(clientX, clientY)
        .map((element) => element.closest?.(tileSelector))
        .find(Boolean) || null

    if (!tile) return null

    const { plane, gridX, gridY } = tile.dataset
    if (!plane || gridX === undefined || gridY === undefined) return null

    return {
      plane,
      x: Number(gridX),
      y: Number(gridY)
    }
  }, [])

  const getHoverPositionFromPoint = useCallback(
    (dragItem, clientX, clientY) => {
      if (!dragItem) return null

      const tile = getTileFromPoint(clientX, clientY)
      if (!tile) return null

      const itemSpace = dragItem.individual_space || 'floor'
      const isWallItem = itemSpace === 'wall' || itemSpace === 'left' || itemSpace === 'right'
      if (
        (itemSpace === 'floor' && tile.plane !== 'floor') ||
        (isWallItem && tile.plane === 'floor')
      ) {
        return null
      }
      const itemWidth = dragItem.itemWidth || dragItem.width || 1
      const itemHeight = dragItem.itemHeight || dragItem.height || 1
      const maxRows = tile.plane === 'floor' ? gridSize : wallRows

      if (
        tile.x < 0 ||
        tile.y < 0 ||
        tile.x + itemWidth > gridSize ||
        tile.y + itemHeight > maxRows
      ) {
        return null
      }

      return {
        plane: tile.plane,
        startX: tile.x,
        startY: tile.y,
        itemWidth,
        itemHeight
      }
    },
    [getTileFromPoint, gridSize, wallRows]
  )

  const handleGhostPointerDown = useCallback(
    (e) => {
      const activeGhostItem = ghostItemRef.current
      if (!activeGhostItem || !isLayoutMode || isPinching) return

      e.preventDefault()
      e.stopPropagation()

      setDraggingItem({ ...activeGhostItem, isGhost: true })
      setHoverPosition(
        getHoverPositionFromPoint(activeGhostItem, e.clientX, e.clientY) || {
          plane: activeGhostItem.plane,
          startX: activeGhostItem.startX,
          startY: activeGhostItem.startY,
          itemWidth: activeGhostItem.itemWidth,
          itemHeight: activeGhostItem.itemHeight
        }
      )
      setPointerDragSession({
        pointerId: e.pointerId,
        type: 'ghost',
        previewX: e.clientX,
        previewY: e.clientY
      })
    },
    [getHoverPositionFromPoint, isLayoutMode, isPinching, setDraggingItem, setHoverPosition]
  )

  useEffect(() => {
    if (!pointerDragSession) return undefined

    const handlePointerMove = (event) => {
      const session = pointerDragSessionRef.current
      if (!session || event.pointerId !== session.pointerId) return

      event.preventDefault()

      setPointerDragSession((prev) =>
        prev
          ? {
              ...prev,
              previewX: event.clientX,
              previewY: event.clientY
            }
          : prev
      )

      const activeItem = ghostItemRef.current
      setHoverPosition(getHoverPositionFromPoint(activeItem, event.clientX, event.clientY))
    }

    const handlePointerEnd = (event) => {
      const session = pointerDragSessionRef.current
      if (!session || event.pointerId !== session.pointerId) return

      event.preventDefault()

      const activeItem = ghostItemRef.current
      const nextHoverPosition =
        getHoverPositionFromPoint(activeItem, event.clientX, event.clientY) ||
        hoverPositionRef.current

      if (nextHoverPosition) {
        moveGhost(nextHoverPosition.startX, nextHoverPosition.startY, nextHoverPosition.plane)
      }

      clearPointerDragSession()
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: false })
    window.addEventListener('pointerup', handlePointerEnd, { passive: false })
    window.addEventListener('pointercancel', handlePointerEnd, { passive: false })

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerEnd)
      window.removeEventListener('pointercancel', handlePointerEnd)
    }
  }, [clearPointerDragSession, getHoverPositionFromPoint, moveGhost, pointerDragSession])

  useEffect(() => {
    if (!isLayoutMode && pointerDragSessionRef.current) {
      clearPointerDragSession()
    }
  }, [clearPointerDragSession, isLayoutMode])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const pageRoot = pageRootRef.current
    const scrollRoot =
      pageRoot?.closest('[data-house-scroll-root="true"]') ||
      document.querySelector('[data-house-scroll-root="true"]')
    const { body, documentElement } = document

    const previousScrollRootOverflow = scrollRoot?.style.overflowY
    const previousScrollRootOverscroll = scrollRoot?.style.overscrollBehavior
    const previousBodyOverflow = body.style.overflow
    const previousBodyOverscroll = body.style.overscrollBehavior
    const previousDocumentOverflow = documentElement.style.overflow
    const previousDocumentOverscroll = documentElement.style.overscrollBehavior

    if (isLayoutMode) {
      if (scrollRoot) {
        scrollRoot.style.overflowY = 'hidden'
        scrollRoot.style.overscrollBehavior = 'none'
      }
      body.style.overflow = 'hidden'
      body.style.overscrollBehavior = 'none'
      documentElement.style.overflow = 'hidden'
      documentElement.style.overscrollBehavior = 'none'
    }

    const handleTouchMove = (event) => {
      if (!isLayoutMode || event.touches.length > 1) return

      const target = event.target
      const allowScroll =
        !pointerDragSessionRef.current &&
        target instanceof Element &&
        target.closest('[data-house-scroll-allow="true"]')

      if (!allowScroll) {
        event.preventDefault()
      }
    }

    document.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      document.removeEventListener('touchmove', handleTouchMove)

      if (scrollRoot) {
        scrollRoot.style.overflowY = previousScrollRootOverflow
        scrollRoot.style.overscrollBehavior = previousScrollRootOverscroll
      }
      body.style.overflow = previousBodyOverflow
      body.style.overscrollBehavior = previousBodyOverscroll
      documentElement.style.overflow = previousDocumentOverflow
      documentElement.style.overscrollBehavior = previousDocumentOverscroll
    }
  }, [isLayoutMode])

  const handleItemClick = (plane, x, y, itemData) => {
    if (ghostItem || editingBackgroundItem || editingConceptItem) return
    convertItemToGhost(plane, itemData)
  }

  const handleConfirmGhost = useCallback(() => {
    if (!ghostItem) return
    const { plane, startX, startY, itemWidth, itemHeight } = ghostItem
    if (!canPlace(plane, startX, startY, itemWidth, itemHeight, null)) {
      setShowInvalidPlacementPopup(true)
      setTimeout(() => setShowInvalidPlacementPopup(false), 2000)
      return
    }
    if (!ghostItem.originalPosition) {
      setItemAttachedAmounts((prev) => {
        const current = prev[ghostItem.id] ?? ghostItem.attached_amount ?? 0
        return { ...prev, [ghostItem.id]: current + 1 }
      })
    }
    confirmGhost()
    setSelectedItemId(null)
  }, [ghostItem, canPlace, confirmGhost, setItemAttachedAmounts])

  const handleCancelGhost = useCallback(() => {
    if (ghostItem?.originalPosition?.positionId) {
      setItemsToDestroy((prev) =>
        Array.from(new Set([...prev, ghostItem.originalPosition.positionId]))
      )
    }
    if (ghostItem?.originalPosition) {
      setItemAttachedAmounts((prev) => {
        const current = prev[ghostItem.id] ?? ghostItem.attached_amount ?? 0
        return { ...prev, [ghostItem.id]: Math.max(0, current - 1) }
      })
    }
    cancelGhost()
    setSelectedItemId(null)
  }, [ghostItem, cancelGhost, setItemsToDestroy, setItemAttachedAmounts])

  const filteredItems = items
    .filter((item) => item.category === selectedCategory)
    .map((item) => {
      const amount = itemAmounts[item.id] ?? item.amount ?? 0
      const attachedAmount = itemAttachedAmounts[item.id] ?? item.attached_amount ?? 0
      const isPinned = amount === attachedAmount && amount > 0

      return {
        ...item,
        amount,
        attached_amount: attachedAmount,
        pinned: isPinned || pinnedItemIds.has(item.id)
      }
    })

  const getCurrentConceptSlots = () => ({
    floor: activeConceptItemId,
    left: activeLeftWallConceptItemId,
    right: activeRightWallConceptItemId
  })

  const applyConceptImagesFromSlots = (slotAssignments) => {
    const floorItem = findConceptItemById(items, slotAssignments.floor)
    const leftItem = findConceptItemById(items, slotAssignments.left)
    const rightItem = findConceptItemById(items, slotAssignments.right)

    setFloorBackgroundImage(getConceptSlotImage(floorItem, 'floor'))
    setLeftWallBackgroundImage(getConceptSlotImage(leftItem, 'left'))
    setRightWallBackgroundImage(getConceptSlotImage(rightItem, 'right'))
  }

  const resetConceptStates = () => {
    setFloorBackgroundImage(null)
    setLeftWallBackgroundImage(null)
    setRightWallBackgroundImage(null)
    setActiveConceptItemId(null)
    setActiveLeftWallConceptItemId(null)
    setActiveRightWallConceptItemId(null)
    setSavedConceptItemId(null)
    setSavedLeftWallConceptItemId(null)
    setSavedRightWallConceptItemId(null)
    setSavedConceptRecordId(null)
    setSavedLeftWallConceptRecordId(null)
    setSavedRightWallConceptRecordId(null)
  }

  const loadPositions = async () => {
    if (!user?.id) return
    if (items.length === 0) return

    try {
      const response = await getUserMaterialItemPositions(user.id)

      if (response.items.length === 0) {
        loadSavedPositions([], items)
        resetConceptStates()
        return
      }
      if (response?.success && response?.items && Array.isArray(response.items)) {
        const specialItems = response.items.filter(
          (item) => item.grid_x === null && item.grid_y === null
        )
        const regularItems = response.items.filter(
          (item) => !(item.grid_x === null && item.grid_y === null)
        )

        const attachedCounts = {}
        regularItems.forEach((posItem) => {
          const itemId = posItem.user_character_cloth_id
          attachedCounts[itemId] = (attachedCounts[itemId] || 0) + 1
        })

        specialItems.forEach((specialItem) => {
          const itemId = specialItem.user_character_cloth_id
          attachedCounts[itemId] = (attachedCounts[itemId] || 0) + 1
        })

        setItemAttachedAmounts(attachedCounts)

        loadSavedPositions(regularItems, items)

        resetConceptStates()

        specialItems.forEach((specialItem) => {
          const bgUrl = specialItem.background_image_url
          const iconUrl = specialItem.icon_url
          if (specialItem.category === 'background') {
            setBackgroundImage(bgUrl || iconUrl)
            setSavedBackgroundImage(bgUrl || iconUrl)
            setSavedBackgroundItemId(specialItem.user_character_cloth_id)
            setSavedBackgroundRecordId(specialItem.id)
          } else if (specialItem.category === 'concept') {
            const itemId = specialItem.user_character_cloth_id
            const slots = getConceptSlots(specialItem)

            if (slots.includes('floor')) {
              setFloorBackgroundImage(specialItem.floor_wall_image_url || iconUrl)
              setActiveConceptItemId(itemId)
              setSavedConceptItemId(itemId)
              setSavedConceptRecordId(specialItem.id)
            }
            if (slots.includes('left')) {
              setLeftWallBackgroundImage(specialItem.left_wall_image_url || specialItem.icon_url)
              setActiveLeftWallConceptItemId(itemId)
              setSavedLeftWallConceptItemId(itemId)
              setSavedLeftWallConceptRecordId(specialItem.id)
            }
            if (slots.includes('right')) {
              setRightWallBackgroundImage(specialItem.right_wall_image_url || specialItem.icon_url)
              setActiveRightWallConceptItemId(itemId)
              setSavedRightWallConceptItemId(itemId)
              setSavedRightWallConceptRecordId(specialItem.id)
            }
          }
        })
      }
    } catch (error) {
      console.error('Error loading saved positions:', error)
    }
  }

  useEffect(() => {
    loadPositions()
  }, [user?.id, items.length])

  const handleRemoveItem = (_itemId, e) => {
    e.stopPropagation()
  }

  const handleTogglePin = (itemId, e) => {
    e.stopPropagation()
    setPinnedItemIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const handleBackgroundSelect = (item) => {
    setTempBackgroundImage(backgroundImage)
    setBackgroundImage(item.background_image_url || item.icon_url)
    setEditingBackgroundItem(item)
  }

  const handleSaveBackgroundItem = () => {
    if (!editingBackgroundItem) return

    const prevId = savedBackgroundItemId
    const newId = editingBackgroundItem.id

    if (prevId !== newId) {
      setItemAttachedAmounts((prev) => {
        const next = { ...prev }
        if (prevId) {
          const prevItem = items.find((i) => i.id === prevId)
          const currentPrevAttached = next[prevId] ?? prevItem?.attached_amount ?? 0
          next[prevId] = Math.max(0, currentPrevAttached - 1)
        }
        const currentNewAttached = next[newId] ?? editingBackgroundItem.attached_amount ?? 0
        next[newId] = currentNewAttached + 1
        return next
      })
      setSavedBackgroundItemId(newId)
    }

    setEditingBackgroundItem(null)
    setTempBackgroundImage(null)
    setSelectedItemId(null)
  }

  const handleCancelBackgroundItem = () => {
    if (tempBackgroundImage !== null) {
      setBackgroundImage(tempBackgroundImage)
    }

    setEditingBackgroundItem(null)
    setTempBackgroundImage(null)
    setSelectedItemId(null)
  }

  const handleConceptSelect = (item) => {
    setTempConceptState({
      floorBackgroundImage,
      leftWallBackgroundImage,
      rightWallBackgroundImage,
      activeConceptItemId,
      activeLeftWallConceptItemId,
      activeRightWallConceptItemId
    })

    const nextSlots = applyConceptItemToSlots(getCurrentConceptSlots(), item)
    applyConceptImagesFromSlots(nextSlots)

    setEditingConceptItem(item)
  }

  const handleSaveConceptItem = () => {
    if (!editingConceptItem) return

    const currentSlots = getCurrentConceptSlots()
    const nextSlots = applyConceptItemToSlots(currentSlots, editingConceptItem)
    const previousItemIds = new Set(Object.values(currentSlots).filter(Boolean))
    const nextItemIds = new Set(Object.values(nextSlots).filter(Boolean))

    setItemAttachedAmounts((prev) => {
      const next = { ...prev }

      previousItemIds.forEach((itemId) => {
        if (nextItemIds.has(itemId)) return

        const prevItem = findConceptItemById(items, itemId)
        next[itemId] = Math.max(0, (next[itemId] ?? prevItem?.attached_amount ?? 0) - 1)
      })

      nextItemIds.forEach((itemId) => {
        if (previousItemIds.has(itemId)) return

        const currentItem = findConceptItemById(items, itemId)
        next[itemId] = (next[itemId] ?? currentItem?.attached_amount ?? 0) + 1
      })

      return next
    })

    setActiveConceptItemId(nextSlots.floor)
    setActiveLeftWallConceptItemId(nextSlots.left)
    setActiveRightWallConceptItemId(nextSlots.right)
    setSelectedItemId(null)
    setEditingConceptItem(null)
    setTempConceptState(null)
  }

  const handleCancelConceptItem = () => {
    if (tempConceptState) {
      setFloorBackgroundImage(tempConceptState.floorBackgroundImage)
      setLeftWallBackgroundImage(tempConceptState.leftWallBackgroundImage)
      setRightWallBackgroundImage(tempConceptState.rightWallBackgroundImage)
      setActiveConceptItemId(tempConceptState.activeConceptItemId)
      setActiveLeftWallConceptItemId(tempConceptState.activeLeftWallConceptItemId)
      setActiveRightWallConceptItemId(tempConceptState.activeRightWallConceptItemId)
    }

    setEditingConceptItem(null)
    setTempConceptState(null)
    setSelectedItemId(null)
  }

  const handleMenuItemClick = (menuId) => {
    if (menuId === 'home') {
      redirectTo(MAIN_PATHS.MyPage)
    } else if (menuId === 'gacha') {
      redirectTo(MAIN_PATHS.CapsuleToyPage)
    } else if (menuId === 'layout') {
      takeSnapshot()
      setSavedBackgroundImage(backgroundImage)
      setLayoutSnapshot({
        backgroundItemId: savedBackgroundItemId,
        floorBackgroundImage,
        leftWallBackgroundImage,
        rightWallBackgroundImage,
        conceptItemId: activeConceptItemId,
        leftWallConceptItemId: activeLeftWallConceptItemId,
        rightWallConceptItemId: activeRightWallConceptItemId,
        itemAmounts: { ...itemAmounts },
        pinnedItemIds: new Set(pinnedItemIds),
        itemAttachedAmounts: { ...itemAttachedAmounts }
      })
      setItemsToDestroy([])
      setIsLayoutMode(true)
    } else if (menuId === 'goout') {
      setIsUserListModalOpen(true)
    }
  }

  const handleSaveAndReturn = async () => {
    const positions = getPositionsForSave()

    itemsToDestroy.forEach((positionId) => {
      positions.push({
        id: positionId,
        _destroy: true
      })
    })

    const backgroundChanged = savedBackgroundItemId !== layoutSnapshot?.backgroundItemId

    if (backgroundChanged && savedBackgroundItemId) {
      if (savedBackgroundRecordId) {
        positions.push({
          id: savedBackgroundRecordId,
          _destroy: true
        })
      }

      positions.push({
        user_character_cloth_id: savedBackgroundItemId,
        grid_x: null,
        grid_y: null,
        index: 1,
        reverse_flag: false
      })
    }

    const conceptChanged =
      activeConceptItemId !== savedConceptItemId ||
      activeLeftWallConceptItemId !== savedLeftWallConceptItemId ||
      activeRightWallConceptItemId !== savedRightWallConceptItemId

    if (conceptChanged) {
      const savedConceptRecordIds = Array.from(
        new Set(
          [
            savedConceptRecordId,
            savedLeftWallConceptRecordId,
            savedRightWallConceptRecordId
          ].filter(Boolean)
        )
      )

      savedConceptRecordIds.forEach((recordId) => {
        positions.push({
          id: recordId,
          _destroy: true
        })
      })

      positions.push(
        ...buildConceptPositionsForSave(
          {
            floor: activeConceptItemId,
            left: activeLeftWallConceptItemId,
            right: activeRightWallConceptItemId
          },
          items
        )
      )
    }

    try {
      if (positions.length > 0) {
        const result = await postSaveMaterialItemPositions({ positions })
        if (result) {
          await Promise.all([loadPositions(), refetch()])
          setSavedBackgroundImage(backgroundImage)
          setItemsToDestroy([])
        }
      } else {
        setItemsToDestroy([])
      }
    } catch (error) {
      console.error('Error saving positions:', error)
    }

    setIsLayoutMode(false)
  }

  const handleReturnWithoutSave = () => {
    cancelGhost()
    handleCancel()
    setBackgroundImage(savedBackgroundImage)
    setSavedBackgroundItemId(layoutSnapshot?.backgroundItemId)
    setFloorBackgroundImage(layoutSnapshot?.floorBackgroundImage)
    setLeftWallBackgroundImage(layoutSnapshot?.leftWallBackgroundImage)
    setRightWallBackgroundImage(layoutSnapshot?.rightWallBackgroundImage)
    setActiveConceptItemId(layoutSnapshot?.conceptItemId)
    setActiveLeftWallConceptItemId(layoutSnapshot?.leftWallConceptItemId)
    setActiveRightWallConceptItemId(layoutSnapshot?.rightWallConceptItemId)
    setItemAmounts(layoutSnapshot?.itemAmounts || {})
    setPinnedItemIds(layoutSnapshot?.pinnedItemIds || new Set())
    setItemAttachedAmounts(layoutSnapshot?.itemAttachedAmounts || {})
    setItemsToDestroy([])
    setSelectedCategory('background')
    setSelectedItemId(null)
    setEditingBackgroundItem(null)
    setTempBackgroundImage(null)
    setEditingConceptItem(null)
    setTempConceptState(null)
    setIsLayoutMode(false)
  }

  const handleSaveAction = () => {
    if (ghostItem) {
      handleConfirmGhost()
      return
    }
    if (editingBackgroundItem) {
      handleSaveBackgroundItem()
      return
    }
    if (editingConceptItem) {
      handleSaveConceptItem()
    }
  }

  const handleCancelAction = () => {
    if (ghostItem) {
      handleCancelGhost()
      return
    }
    if (editingBackgroundItem) {
      handleCancelBackgroundItem()
      return
    }
    if (editingConceptItem) {
      handleCancelConceptItem()
    }
  }

  const handleRotateAction = () => {
    if (ghostItem) rotateGhost()
  }

  const events = useQuery('events/mypage', () => getCurrentEvents(), {
    cacheTime: 0,
    select: (data) =>
      data.filter((event) => !event.displayPeriodSetting && event.displayOnTheMyPage)
  })

  const rightMenuItems = [
    { id: 'layout', label: 'レイアウト', icon: LayoutImg },
    { id: 'goout', label: 'お出かけ', icon: GoOutImg },
    { id: 'gacha', label: 'ガチャ', icon: GachaImg },
    { id: 'home', label: 'HOME', icon: HomeImg }
  ]

  const houseGrid3DProps = {
    containerRef,
    isPinching,
    gridSize,
    wallRows,
    tileSize,
    size,
    wallHeight,
    isLayoutMode,
    hoverPosition,
    draggingItem,
    justDroppedKey,
    getItemAtPosition,
    isTileHovered,
    getItemsForPlane,
    onItemClick: handleItemClick,
    ghostItem,
    onGhostPointerDown: handleGhostPointerDown,
    floorBackgroundImage,
    leftWallBackgroundImage,
    rightWallBackgroundImage
  }

  return (
    <Wrapper data-house-scroll-root="true">
      <MainLayout showHeader={false} showFooter={false}>
        <div
          ref={pageRootRef}
          className="ios-media-guard flex items-center justify-center relative"
          onContextMenu={(e) => e.preventDefault()}
          style={{ height: '100dvh', width: '100%' }}
        >
          <div
            className="ios-media-guard absolute inset-0 z-0"
            onContextMenu={(e) => e.preventDefault()}
          >
            <img
              src={backgroundImage || BG}
              alt="background"
              className="h-full w-full object-cover"
              draggable={false}
            />
          </div>
          {!isLayoutMode && (
            <HouseHeader
              user={user}
              isUIOn={isUIOn}
              onUIToggle={() => setIsUIOn(!isUIOn)}
              onProfileClick={() => setIsUserProfileModalOpen(true)}
              onRankUpClick={() => setIsRankUpModalOpen(true)}
              onHelpClick={() => setIsHelpModalOpen(true)}
              events={events.data}
            />
          )}
          {!isLayoutMode && isUIOn && (
            <HouseRightMenu
              menuItems={rightMenuItems}
              onMenuItemClick={handleMenuItemClick}
              setIsRankUpModalOpen={setIsRankUpModalOpen}
              showRankUpButton={user?.ranking !== 'gold'}
            />
          )}
          {isLayoutMode && isUIOn && (
            <HouseLayoutControls
              onSaveAndReturn={handleSaveAndReturn}
              onReturnWithoutSave={handleReturnWithoutSave}
              isDisabledSave={Boolean(ghostItem || editingBackgroundItem || editingConceptItem)}
            />
          )}
          {isLayoutMode && isUIOn ? (
            <div className="flex flex-col w-full h-full z-10">
              <div className="relative z-30 flex-1 flex items-end min-h-0">
                <HouseGrid3D {...houseGrid3DProps} />
              </div>
              <div className="relative z-10 flex-1 min-h-0">
                <HouseItemsPanel
                  categories={materialCategories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  filteredItems={filteredItems}
                  selectedItemId={selectedItemId}
                  onItemSelect={setSelectedItemId}
                  onTogglePin={handleTogglePin}
                  onRemoveItem={handleRemoveItem}
                  onCancel={() => {
                    cancelGhost()
                    setSelectedItemId(null)
                  }}
                  onBackgroundSelect={handleBackgroundSelect}
                  editingBackgroundItem={editingBackgroundItem}
                  onConceptSelect={handleConceptSelect}
                  editingConceptItem={editingConceptItem}
                  ghostItem={ghostItem}
                  onPlaceGhost={handlePlaceGhost}
                />
              </div>
            </div>
          ) : (
            <HouseGrid3D {...houseGrid3DProps} />
          )}

          {pointerDragSession?.type === 'ghost' && (
            <div
              className="fixed left-0 top-0 z-[999] pointer-events-none"
              style={{
                transform: `translate(${pointerDragSession.previewX - 48}px, ${
                  pointerDragSession.previewY - 48
                }px)`
              }}
            >
              <img
                src={ghostItem?.displayImage || ghostItem?.icon_url}
                alt=""
                className="ios-media-guard w-24 h-24 object-contain opacity-80"
                draggable={false}
              />
            </div>
          )}

          {isLayoutMode && isUIOn && (ghostItem || editingBackgroundItem || editingConceptItem) && (
            <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 flex gap-4">
              <button onClick={handleSaveAction} className="hover:opacity-80 transition-opacity">
                <img src={BtnLayoutFix} alt="保存" className="w-16 h-16" />
              </button>
              <button onClick={handleCancelAction} className="hover:opacity-80 transition-opacity">
                <img src={BtnLayoutCancel} alt="キャンセル" className="w-16 h-16" />
              </button>
              {ghostItem && (
                <button
                  onClick={handleRotateAction}
                  className="hover:opacity-80 transition-opacity"
                >
                  <img src={BtnLayoutReverse} alt="回転" className="w-16 h-16" />
                </button>
              )}
            </div>
          )}

          {isUIOn && (
            <>
              <ModalUserProfile
                open={isUserProfileModalOpen}
                onClose={() => setIsUserProfileModalOpen(false)}
                user={user}
                materialItems={rawMaterialItems}
              />

              <ModalUserList
                open={isUserListModalOpen}
                onClose={() => setIsUserListModalOpen(false)}
              />

              <ModalHelp open={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />

              <ModalRankUp
                open={isRankUpModalOpen}
                onClose={() => setIsRankUpModalOpen(false)}
                userId={user.id}
                userRank={user.ranking}
                userRedAmount={user.red_amount}
              />
            </>
          )}
          {showInvalidPlacementPopup && (
            <div className="fixed flex items-center gap-2 top-20 z-[70] px-6 py-2 bg-black/60 rounded-md font-ShinMaruGo">
              <div className="w-5 h-5">
                <img src={IconCaution} alt="caution" className="h-full w-full object-cover" />
              </div>
              <p className="text-base font-bold text-house-pink tracking-wider text-center">
                配置することができません
              </p>
            </div>
          )}
        </div>
      </MainLayout>
    </Wrapper>
  )
}
